'use client';

/**
 * IntakeStepRenderer Component
 *
 * Renders fields from an intake step template dynamically.
 *
 * @see Issue #432 - Templated Loan Intake Wizard
 */

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Rating from '@mui/material/Rating';
import Slider from '@mui/material/Slider';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

// Icon map for radio-cards field type (resolves icon name strings to components)
const RADIO_CARD_ICON_MAP: Record<string, React.ReactElement> = {
  TrendingUp: <TrendingUpIcon />,
  Storefront: <StorefrontIcon />,
  Warehouse: <WarehouseIcon />,
  ArrowForward: <ArrowForwardIcon />,
  SwapHoriz: <SwapHorizIcon />,
};

import type {
  IntakeCondition,
  IntakeConditionGroup,
  IntakeFieldTemplate,
  IntakeStepTemplate,
} from '@ops-matrix/shared-types';
import { useI18n } from '../../../hooks/useI18n';
import {
  ContractSelectionCard,
  type ContractSelectionOption,
  type ContractTemplateChoice,
  type TemplatePreviewData,
} from '../../ContractSelectionCard/ContractSelectionCard';
import { getI18nString } from '../intake-utils';

// ============================================================================
// Local Types for Extended Field Configurations
// These mirror the types from shared-types for type safety
// ============================================================================

interface PricingTier {
  value: string;
  label: string;
  description: string;
  price: string;
  annualPrice?: string;
  features: string[];
  recommended?: boolean;
  badge?: string;
}

interface PricingCardsConfig {
  tiers: PricingTier[];
  showBillingCycleToggle?: boolean;
  billingCycleFieldName?: string;
}

interface SummaryField {
  name: string;
  label: string;
  format?: 'text' | 'currency' | 'date' | 'boolean' | 'list' | 'lookup' | 'percentage';
  lookupMap?: Record<string, string>;
}

interface SummarySection {
  title: string;
  fields: SummaryField[];
}

interface SummaryConfig {
  sections: SummarySection[];
  showEditButtons?: boolean;
}

interface DynamicSelectConfig {
  dataSource: string;
  valueField?: string;
  labelField?: string;
  descriptionField?: string;
  iconField?: string;
  multiple?: boolean;
  emptyMessage?: string;
  loadingMessage?: string;
  managementLink?: {
    href: string;
    labelKey: string;
  };
}

interface RadioCardOption {
  value: string;
  label: string;
  description?: string;
  icon?: string;
  recommended?: boolean;
}

interface RadioCardsConfig {
  options: RadioCardOption[];
  orientation?: 'horizontal' | 'vertical';
  columns?: number;
}

// Extended field template with new properties
interface ExtendedFieldTemplate extends IntakeFieldTemplate {
  dynamicSelectConfig?: DynamicSelectConfig;
  pricingCardsConfig?: PricingCardsConfig;
  summaryConfig?: SummaryConfig;
  radioCardsConfig?: RadioCardsConfig;
}

import type React from 'react';
import { useMemo, useState as useReactState } from 'react';

import type { NeonAccent } from '../../../theme/palette';
import { MagicInput } from '../../MagicInput';
import { MagicSelect } from '../../MagicSelect';
import { MaskedInput } from '../../MaskedInput';

// ============================================================================
// Types
// ============================================================================

/**
 * Dynamic options provided by parent component for dynamic-select fields
 */
export interface DynamicSelectOption {
  value: string;
  label: string;
  /** Additional data from the source record */
  data?: Record<string, unknown>;
}

/**
 * Map of dataSource keys to their loaded options
 */
export type DynamicOptionsMap = Record<
  string,
  {
    options: DynamicSelectOption[];
    loading?: boolean;
    error?: string;
  }
>;

export interface IntakeStepRendererProps {
  /** Step template */
  step: IntakeStepTemplate;
  /** Current form data */
  formData: Record<string, unknown>;
  /** Change handler */
  onChange: (name: string, value: unknown) => void;
  /** Field errors */
  errors?: Record<string, string>;
  /** Whether fields are disabled */
  disabled?: boolean;
  /** Neon accent color */
  neonAccent?: NeonAccent;
  // Analytics callbacks (P2 - Issue #471)
  /** Called when field receives focus */
  onFieldFocus?: (fieldName: string) => void;
  /** Called when field loses focus */
  onFieldBlur?: (fieldName: string, fieldType: string, hasValue: boolean) => void;
  // Dynamic options support (Business Creation)
  /** Dynamic options loaded from API, keyed by dataSource */
  dynamicOptions?: DynamicOptionsMap;
  /** Lookup maps for summary display (e.g., realmId -> realm name) */
  lookupMaps?: Record<string, Record<string, string>>;
  /**
   * Issue #1382 — Optional callback to fetch an inline renderable preview
   * for a template when the user clicks a card's "preview" action.
   * Wired through to `ContractSelectionCard.onFetchPreview`.
   */
  onFetchTemplatePreview?: (templateId: string) => Promise<TemplatePreviewData>;
  /**
   * Issue #1540 / #1533 Phase 3 — Optional bundle that turns the
   * contract-type cards into a two-level picker (outer = contract type,
   * inner = template+version dropdown). Wired through verbatim to
   * `ContractSelectionCard`.
   */
  contractSelectionExtras?: ContractSelectionExtras;
}

/**
 * Optional callbacks/props that turn the contract-type card grid into a
 * two-level picker. Issue #1540 / #1533 Phase 3.
 */
export interface ContractSelectionExtras {
  /**
   * Map keyed by `option.value` (contractTypeDefinitionId) → list of
   * templates to show in the per-card dropdown. Default-for-type row first.
   */
  templatesPerOption?: Record<string, ContractTemplateChoice[]>;
  /** Currently chosen templateId per option, keyed by `option.value`. */
  selectedTemplateByOption?: Record<string, string>;
  /** Called when the user changes the per-card template dropdown. */
  onTemplateSelectChange?: (optionValue: string, templateId: string) => void;
  /** Per-option loading flag for the dropdown. */
  templatesLoadingByOption?: Record<string, boolean>;
  /** Inline "Upload custom" link rendered next to the dropdown. */
  uploadCustomLink?: {
    hrefPattern: string;
    labelKey: string;
    contractTypeFromOption?: (option: ContractSelectionOption) => string | undefined;
  };
}

// ============================================================================
// Styled Components
// ============================================================================

const FieldContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(0.5),
  position: 'relative',
}));

const StyledFormLabel = styled(FormLabel)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  display: 'block',
  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : theme.palette.text.primary,
  fontSize: '0.875rem',
  fontWeight: 500,
}));

const RadioOption = styled(FormControlLabel)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  marginLeft: 0,
  padding: theme.spacing(0.5, 0),
  '& .MuiFormControlLabel-label': {
    fontSize: '0.875rem',
  },
  // Ensure click target is well-defined
  '& .MuiRadio-root': {
    padding: theme.spacing(1),
  },
}));

const CheckboxOption = styled(FormControlLabel)(() => ({
  '& .MuiFormControlLabel-label': {
    fontSize: '0.875rem',
  },
}));

const RadioGroupContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
}));

// ============================================================================
// Pricing Cards Styled Components
// ============================================================================

const PricingCardsContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

// Issue #1562 / #130 adjacent-sweep: PricingCard renders as a `ButtonBase`
// (real interactive primitive with native click + Space/Enter handling) so
// headless Chromium / React 19 dispatches the click cleanly without needing
// fiber-walk fallbacks. `display: 'flex'`, `alignItems: 'stretch'`,
// `textAlign: 'left'` preserve the column-card layout under ButtonBase's
// default inline-flex.
const PricingCard = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== '$selected' && prop !== '$recommended',
})<{ $selected?: boolean; $recommended?: boolean }>(({ theme, $selected, $recommended }) => ({
  position: 'relative',
  padding: theme.spacing(2.5),
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  border: `2px solid ${$selected ? theme.palette.primary.main : theme.palette.divider}`,
  backgroundColor: $selected
    ? theme.palette.mode === 'dark'
      ? 'rgba(25, 118, 210, 0.08)'
      : 'rgba(25, 118, 210, 0.04)'
    : theme.palette.background.paper,
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  textAlign: 'left',
  width: '100%',
  '&:hover': {
    borderColor: $selected ? theme.palette.primary.main : theme.palette.primary.light,
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
  ...($recommended && {
    borderColor: $selected ? theme.palette.primary.main : theme.palette.warning.main,
  }),
}));

const PricingBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: -12,
  left: '50%',
  transform: 'translateX(-50%)',
  padding: theme.spacing(0.5, 1.5),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.warning.main,
  color: theme.palette.warning.contrastText,
  fontSize: '0.75rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  whiteSpace: 'nowrap',
}));

const PricingPrice = styled(Typography)(({ theme }) => ({
  fontSize: '1.75rem',
  fontWeight: 700,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(0.5),
}));

const PricingFeatureList = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  '& > div': {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.75),
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    '&::before': {
      content: '"✓"',
      color: theme.palette.success.main,
      fontWeight: 600,
      flexShrink: 0,
    },
  },
}));

// ============================================================================
// Summary Styled Components
// ============================================================================

const SummaryContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
  border: `1px solid ${theme.palette.divider}`,
}));

const SummarySectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: theme.spacing(1.5),
  paddingBottom: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const SummaryRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: theme.spacing(0.75, 0),
  '&:not(:last-child)': {
    marginBottom: theme.spacing(0.5),
  },
}));

const SummaryLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  flexShrink: 0,
  marginRight: theme.spacing(2),
}));

const SummaryValue = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
  textAlign: 'right',
  wordBreak: 'break-word',
}));

// ============================================================================
// Field Renderers
// ============================================================================

interface FieldProps {
  field: ExtendedFieldTemplate;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
  disabled?: boolean;
  neonAccent?: NeonAccent;
  // Analytics callbacks (P2 - Issue #471)
  onFocus?: () => void;
  onBlur?: () => void;
  // Dynamic options support (Business Creation)
  dynamicOptions?: DynamicOptionsMap;
  lookupMaps?: Record<string, Record<string, string>>;
  allFormData?: Record<string, unknown>;
  // Issue #1382 — inline template preview
  onFetchTemplatePreview?: (templateId: string) => Promise<TemplatePreviewData>;
  // Issue #1540 — two-level contract+template picker
  contractSelectionExtras?: ContractSelectionExtras;
  // Translator from the calling component's `useI18n()`. Threaded in
  // because `renderField` is a plain function (not a component) and
  // can't call hooks directly. Used for a11y strings (aria-labels) on
  // controls whose visible label is data-driven.
  t: (key: string, vars?: Record<string, string | number>) => string;
}

/** Password field with visibility toggle */
function PasswordField({
  value,
  onChange,
  label,
  placeholder,
  helperText,
  error,
  disabled,
  required,
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  disabled?: boolean;
  required?: boolean;
}) {
  const [showPassword, setShowPassword] = useReactState(false);
  const { t } = useI18n();
  return (
    <MagicInput
      type={showPassword ? 'text' : 'password'}
      value={value}
      onChange={onChange}
      label={label}
      placeholder={placeholder}
      helperText={helperText}
      error={error}
      disabled={disabled}
      required={required}
      color="primary"
      slotProps={{
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
                size="small"
                aria-label={showPassword ? t('common.hidePassword') : t('common.showPassword')}
                aria-pressed={showPassword}
              >
                {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
}

const renderField = ({
  field,
  value,
  onChange,
  error,
  disabled,
  neonAccent: _neonAccent,
  onFocus,
  onBlur,
  dynamicOptions,
  lookupMaps,
  allFormData,
  onFetchTemplatePreview,
  contractSelectionExtras,
  t,
}: FieldProps): React.ReactNode => {
  // Merge global disabled with per-field disabled (field.disabled from template)
  const isDisabled = disabled || field.disabled === true;
  const stringValue = value != null ? String(value) : '';
  const numberValue = typeof value === 'number' ? value : undefined;

  // Analytics event handlers (P2 - Issue #471)
  const handleFocus = () => onFocus?.();
  const handleBlur = () => onBlur?.();

  switch (field.type) {
    case 'text':
    case 'email':
    case 'phone':
      return (
        <MagicInput
          value={stringValue}
          onChange={(e) => onChange(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          label={getI18nString(field.label)}
          placeholder={getI18nString(field.placeholder)}
          helperText={error || getI18nString(field.helpText)}
          error={Boolean(error)}
          disabled={isDisabled}
          required={field.required}
          type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
          color="primary"
        />
      );

    case 'number':
      return (
        <MagicInput
          value={numberValue !== undefined ? String(numberValue) : ''}
          onChange={(e) => {
            const num = parseFloat(e.target.value);
            onChange(Number.isNaN(num) ? undefined : num);
          }}
          label={getI18nString(field.label)}
          placeholder={getI18nString(field.placeholder)}
          helperText={error || getI18nString(field.helpText)}
          error={Boolean(error)}
          disabled={isDisabled}
          required={field.required}
          type="number"
          color="primary"
        />
      );

    case 'currency':
      return (
        <MagicInput
          value={numberValue !== undefined ? String(numberValue) : ''}
          onChange={(e) => {
            const val = e.target.value.replace(/[^0-9.]/g, '');
            const num = parseFloat(val);
            onChange(Number.isNaN(num) ? undefined : num);
          }}
          label={getI18nString(field.label)}
          placeholder={getI18nString(field.placeholder) || '$0'}
          helperText={error || getI18nString(field.helpText)}
          error={Boolean(error)}
          disabled={isDisabled}
          required={field.required}
          type="text"
          slotProps={{
            input: {
              startAdornment: <Box sx={{ mr: 1, color: 'text.secondary' }}>$</Box>,
            },
          }}
          color="primary"
        />
      );

    case 'percentage':
      return (
        <MagicInput
          value={numberValue !== undefined ? String(numberValue) : ''}
          onChange={(e) => {
            const num = parseFloat(e.target.value);
            onChange(Number.isNaN(num) ? undefined : num);
          }}
          label={getI18nString(field.label)}
          placeholder={getI18nString(field.placeholder)}
          helperText={error || getI18nString(field.helpText)}
          error={Boolean(error)}
          disabled={isDisabled}
          required={field.required}
          type="number"
          slotProps={{
            input: {
              endAdornment: <Box sx={{ ml: 1, color: 'text.secondary' }}>%</Box>,
            },
          }}
          color="primary"
        />
      );

    case 'select':
      return (
        <MagicSelect
          value={stringValue}
          onChange={(e) => onChange(e.target.value)}
          label={getI18nString(field.label)}
          options={field.options?.map((opt) => ({ value: opt.value, label: getI18nString(opt.label) })) || []}
          helperText={error || getI18nString(field.helpText)}
          error={Boolean(error)}
          disabled={isDisabled}
          required={field.required}
          color="primary"
        />
      );

    case 'multi-select': {
      // Value should be an array of selected values.
      // Source either static `field.options` OR `dynamicSelectConfig.dataSource`
      // (e.g. tenant-specific loan products) — whichever is set. Static
      // options take precedence so legacy templates keep working.
      const selectedValues = Array.isArray(value) ? value : [];
      const dynamicConfig = field.dynamicSelectConfig;
      const dynamicSource = dynamicConfig ? dynamicOptions?.[dynamicConfig.dataSource] : undefined;
      const dynamicLoading = dynamicSource?.loading ?? false;
      const dynamicError = dynamicSource?.error;
      const resolvedOptions =
        field.options && field.options.length > 0
          ? field.options.map((o) => ({ value: o.value, label: getI18nString(o.label) }))
          : (dynamicSource?.options ?? []).map((o) => ({ value: o.value, label: o.label }));

      const helperText = dynamicError || error || getI18nString(field.helpText);
      // An empty body should mean "no options". When a dynamic source failed,
      // surface the failure in the body too — the helperText alone is easy to
      // miss and "No options available" misrepresents the cause.
      const showEmpty = !dynamicLoading && !dynamicError && resolvedOptions.length === 0;

      return (
        <FormControl error={Boolean(error || dynamicError)} disabled={isDisabled} required={field.required} fullWidth>
          <StyledFormLabel>{getI18nString(field.label)}</StyledFormLabel>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
            {dynamicLoading ? (
              <Typography variant="body2" color="text.secondary">
                {dynamicConfig?.loadingMessage ?? 'Loading…'}
              </Typography>
            ) : dynamicError ? (
              <Typography variant="body2" color="error">
                {dynamicError}
              </Typography>
            ) : showEmpty ? (
              <Typography variant="body2" color="text.secondary">
                {dynamicConfig?.emptyMessage ?? 'No options available'}
              </Typography>
            ) : (
              resolvedOptions.map((opt) => (
                <CheckboxOption
                  key={opt.value}
                  control={
                    <Checkbox
                      checked={selectedValues.includes(opt.value)}
                      onChange={(e) => {
                        const newValues = e.target.checked
                          ? [...selectedValues, opt.value]
                          : selectedValues.filter((v: string) => v !== opt.value);
                        onChange(newValues);
                      }}
                    />
                  }
                  label={opt.label}
                />
              ))
            )}
          </Box>
          {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
      );
    }

    case 'radio':
      return (
        <FormControl error={Boolean(error)} disabled={isDisabled} required={field.required} fullWidth>
          <StyledFormLabel>{getI18nString(field.label)}</StyledFormLabel>
          <RadioGroupContainer>
            <RadioGroup value={stringValue} onChange={(e) => onChange(e.target.value)}>
              {field.options?.map((opt) => (
                <RadioOption
                  key={opt.value}
                  value={opt.value}
                  control={<Radio size="small" />}
                  label={getI18nString(opt.label)}
                />
              ))}
            </RadioGroup>
          </RadioGroupContainer>
          {(error || field.helpText) && (
            <FormHelperText sx={{ mt: 1 }}>{error || getI18nString(field.helpText)}</FormHelperText>
          )}
        </FormControl>
      );

    case 'checkbox':
      return (
        <FormControl error={Boolean(error)} disabled={isDisabled}>
          <CheckboxOption
            control={<Checkbox checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} />}
            label={getI18nString(field.label)}
          />
          {(error || field.helpText) && <FormHelperText>{error || getI18nString(field.helpText)}</FormHelperText>}
        </FormControl>
      );

    case 'date':
      return (
        <MagicInput
          value={stringValue}
          onChange={(e) => onChange(e.target.value)}
          label={getI18nString(field.label)}
          helperText={error || getI18nString(field.helpText)}
          error={Boolean(error)}
          disabled={isDisabled}
          required={field.required}
          type="date"
          slotProps={{ inputLabel: { shrink: true } }}
          color="primary"
        />
      );

    case 'ssn':
      return (
        <MaskedInput
          mask="ssn"
          value={stringValue}
          onChange={onChange}
          label={getI18nString(field.label)}
          helperText={error || getI18nString(field.helpText)}
          error={Boolean(error)}
          disabled={isDisabled}
          required={field.required}
          showToggle
          color="primary"
        />
      );

    case 'address': {
      // Address is complex - render as multiple fields
      const addressValue = (value as Record<string, string>) || {};
      return (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <MagicInput
              value={addressValue['street1'] || ''}
              onChange={(e) => onChange({ ...addressValue, street1: e.target.value })}
              label="Street Address"
              placeholder="123 Main St"
              disabled={isDisabled}
              required={field.required}
              color="primary"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <MagicInput
              value={addressValue['street2'] || ''}
              onChange={(e) => onChange({ ...addressValue, street2: e.target.value })}
              label="Apt, Suite, Unit (optional)"
              disabled={isDisabled}
              color="primary"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 5 }}>
            <MagicInput
              value={addressValue['city'] || ''}
              onChange={(e) => onChange({ ...addressValue, city: e.target.value })}
              label="City"
              disabled={isDisabled}
              required={field.required}
              color="primary"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <MagicInput
              value={addressValue['state'] || ''}
              onChange={(e) => onChange({ ...addressValue, state: e.target.value.toUpperCase().slice(0, 2) })}
              label="State"
              placeholder="CA"
              disabled={isDisabled}
              required={field.required}
              slotProps={{ htmlInput: { maxLength: 2 } }}
              color="primary"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4 }}>
            <MagicInput
              value={addressValue['postalCode'] || ''}
              onChange={(e) => onChange({ ...addressValue, postalCode: e.target.value })}
              label="ZIP Code"
              placeholder="90210"
              disabled={isDisabled}
              required={field.required}
              slotProps={{ htmlInput: { maxLength: 10 } }}
              color="primary"
            />
          </Grid>
          {error && (
            <Grid size={{ xs: 12 }}>
              <FormHelperText error>{error}</FormHelperText>
            </Grid>
          )}
        </Grid>
      );
    }

    // ========================================================================
    // Extended Field Types (P2 - Issue #471)
    // ========================================================================

    case 'textarea':
      return (
        <TextField
          value={stringValue}
          onChange={(e) => onChange(e.target.value)}
          label={getI18nString(field.label)}
          placeholder={getI18nString(field.placeholder)}
          helperText={error || getI18nString(field.helpText)}
          error={Boolean(error)}
          disabled={isDisabled}
          required={field.required}
          multiline
          rows={field.validation?.minLength ? Math.ceil(field.validation.minLength / 50) : 3}
          fullWidth
          variant="outlined"
        />
      );

    case 'file': {
      // File upload field
      const fileInputId = `file-input-${field.id}`;
      const fileValue = value as { name: string; size: number; type: string } | null;
      return (
        <FormControl error={Boolean(error)} disabled={isDisabled} required={field.required} fullWidth>
          <StyledFormLabel>{getI18nString(field.label)}</StyledFormLabel>
          {/* Issue #1562 adjacent-sweep: switching the dropzone from `<Box onClick>`
              to `<ButtonBase>` so headless Chromium / React 19 dispatches the click
              cleanly without needing fiber-walk fallbacks.
              `component="div"` is REQUIRED here (PR #1564 Copilot review): the
              dropzone contains a clear-`<IconButton>` that mounts when a file is
              selected. Letting ButtonBase render as its default `<button>` would
              create button-in-button (invalid HTML, breaks keyboard / screen reader).
              ButtonBase still attaches a real DOM click + native Space/Enter
              keyboard handler when `component` overrides the root.
              The inner clear-IconButton already calls `e.stopPropagation()`. */}
          <ButtonBase
            component="div"
            sx={{
              display: 'block',
              width: '100%',
              border: '2px dashed',
              borderColor: error ? 'error.main' : 'divider',
              borderRadius: 1,
              p: 2,
              textAlign: 'center',
              cursor: disabled ? 'not-allowed' : 'pointer',
              bgcolor: 'action.hover',
              '&:hover': { bgcolor: 'action.selected' },
            }}
            onClick={() => document.getElementById(fileInputId)?.click()}
            disabled={disabled}
            tabIndex={disabled ? -1 : 0}
            role="button"
            // Prefer the field's own localized label when present (already
            // processed via `getI18nString`); fall back to a generic
            // localized "Upload file" string so screen-reader users get
            // translated text matching the rest of the intake wizard.
            aria-label={getI18nString(field.label) || t('intake.field.uploadFileAriaLabel')}
          >
            <input
              id={fileInputId}
              type="file"
              style={{ display: 'none' }}
              accept={field.validation?.allowedTypes?.join(',')}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  onChange({ name: file.name, size: file.size, type: file.type });
                }
              }}
            />
            {fileValue ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <Typography variant="body2">{fileValue.name}</Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  ({(fileValue.size / 1024).toFixed(1)} KB)
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(null);
                  }}
                >
                  ✕
                </IconButton>
              </Box>
            ) : (
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Click to upload or drag and drop
                </Typography>
                {field.validation?.allowedTypes && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {field.validation.allowedTypes.join(', ')}
                  </Typography>
                )}
              </Box>
            )}
          </ButtonBase>
          {(error || field.helpText) && <FormHelperText>{error || getI18nString(field.helpText)}</FormHelperText>}
        </FormControl>
      );
    }

    case 'signature': {
      // Signature capture field (placeholder - requires canvas implementation)
      const signatureValue = value as string | null;
      return (
        <FormControl error={Boolean(error)} disabled={isDisabled} required={field.required} fullWidth>
          <StyledFormLabel>{getI18nString(field.label)}</StyledFormLabel>
          <Box
            sx={{
              border: '1px solid',
              borderColor: error ? 'error.main' : 'divider',
              borderRadius: 1,
              height: 120,
              bgcolor: 'background.paper',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
            }}
          >
            {signatureValue ? (
              <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
                <img
                  src={signatureValue}
                  alt="Signature"
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
                <IconButton size="small" sx={{ position: 'absolute', top: 4, right: 4 }} onClick={() => onChange(null)}>
                  ✕
                </IconButton>
              </Box>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Sign here (signature capture requires canvas support)
              </Typography>
            )}
          </Box>
          {(error || field.helpText) && <FormHelperText>{error || getI18nString(field.helpText)}</FormHelperText>}
        </FormControl>
      );
    }

    case 'rating': {
      // Star rating field
      const ratingValue = typeof value === 'number' ? value : 0;
      const maxRating = field.validation?.max || 5;
      return (
        <FormControl error={Boolean(error)} disabled={isDisabled} required={field.required}>
          <StyledFormLabel>{getI18nString(field.label)}</StyledFormLabel>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Rating
              value={ratingValue}
              onChange={(_e, newValue) => onChange(newValue)}
              max={maxRating}
              disabled={isDisabled}
              size="large"
            />
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {ratingValue} / {maxRating}
            </Typography>
          </Box>
          {(error || field.helpText) && <FormHelperText>{error || getI18nString(field.helpText)}</FormHelperText>}
        </FormControl>
      );
    }

    case 'slider': {
      // Slider/range field
      const sliderValue = typeof value === 'number' ? value : field.validation?.min || 0;
      const min = field.validation?.min || 0;
      const max = field.validation?.max || 100;
      const step = field.validation?.step || 1;
      return (
        <FormControl error={Boolean(error)} disabled={isDisabled} required={field.required} fullWidth>
          <StyledFormLabel>{getI18nString(field.label)}</StyledFormLabel>
          <Box sx={{ px: 1, pt: 1 }}>
            <Slider
              value={sliderValue}
              onChange={(_e, newValue) => onChange(newValue)}
              min={min}
              max={max}
              step={step}
              disabled={isDisabled}
              valueLabelDisplay="auto"
              marks={[
                { value: min, label: String(min) },
                { value: max, label: String(max) },
              ]}
            />
          </Box>
          {(error || field.helpText) && <FormHelperText>{error || getI18nString(field.helpText)}</FormHelperText>}
        </FormControl>
      );
    }

    case 'date-range': {
      // Date range picker
      const dateRangeValue = (value as { start?: string; end?: string }) || {};
      return (
        <FormControl error={Boolean(error)} disabled={isDisabled} required={field.required} fullWidth>
          <StyledFormLabel>{getI18nString(field.label)}</StyledFormLabel>
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <MagicInput
              value={dateRangeValue.start || ''}
              onChange={(e) => onChange({ ...dateRangeValue, start: e.target.value })}
              label="Start Date"
              type="date"
              disabled={isDisabled}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ flex: 1 }}
              color="primary"
            />
            <MagicInput
              value={dateRangeValue.end || ''}
              onChange={(e) => onChange({ ...dateRangeValue, end: e.target.value })}
              label="End Date"
              type="date"
              disabled={isDisabled}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ flex: 1 }}
              color="primary"
            />
          </Box>
          {(error || field.helpText) && <FormHelperText>{error || getI18nString(field.helpText)}</FormHelperText>}
        </FormControl>
      );
    }

    case 'time':
      return (
        <MagicInput
          value={stringValue}
          onChange={(e) => onChange(e.target.value)}
          label={getI18nString(field.label)}
          helperText={error || getI18nString(field.helpText)}
          error={Boolean(error)}
          disabled={isDisabled}
          required={field.required}
          type="time"
          slotProps={{ inputLabel: { shrink: true } }}
          color="primary"
        />
      );

    case 'url':
      return (
        <MagicInput
          value={stringValue}
          onChange={(e) => onChange(e.target.value)}
          label={getI18nString(field.label)}
          placeholder={getI18nString(field.placeholder) || 'https://'}
          helperText={error || getI18nString(field.helpText)}
          error={Boolean(error)}
          disabled={isDisabled}
          required={field.required}
          type="url"
          color="primary"
        />
      );

    case 'color': {
      // Color picker
      return (
        <FormControl error={Boolean(error)} disabled={isDisabled} required={field.required}>
          <StyledFormLabel>{getI18nString(field.label)}</StyledFormLabel>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <input
              type="color"
              value={stringValue || '#000000'}
              onChange={(e) => onChange(e.target.value)}
              disabled={isDisabled}
              style={{ width: 48, height: 36, border: 'none', cursor: 'pointer' }}
            />
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {stringValue || '#000000'}
            </Typography>
          </Box>
          {(error || field.helpText) && <FormHelperText>{error || getI18nString(field.helpText)}</FormHelperText>}
        </FormControl>
      );
    }

    case 'rich-text': {
      // Rich text editor (placeholder - requires TinyMCE/Quill integration)
      return (
        <FormControl error={Boolean(error)} disabled={isDisabled} required={field.required} fullWidth>
          <StyledFormLabel>{getI18nString(field.label)}</StyledFormLabel>
          <TextField
            value={stringValue}
            onChange={(e) => onChange(e.target.value)}
            placeholder={getI18nString(field.placeholder)}
            multiline
            rows={5}
            fullWidth
            variant="outlined"
            disabled={isDisabled}
            helperText="Rich text editor - formatting not yet available"
          />
          {(error || field.helpText) && <FormHelperText>{error || getI18nString(field.helpText)}</FormHelperText>}
        </FormControl>
      );
    }

    case 'hidden':
      // Hidden field - render nothing but include value
      return null;

    // ========================================================================
    // Admin Intake Field Types (Business Creation)
    // ========================================================================

    case 'dynamic-select': {
      // Dynamic select with options loaded from API
      const config = field.dynamicSelectConfig;
      if (!config) {
        return (
          <Typography color="error" variant="body2">
            Error: dynamicSelectConfig is required for dynamic-select field
          </Typography>
        );
      }

      const sourceData = dynamicOptions?.[config.dataSource];
      const options = sourceData?.options || [];
      const isLoading = sourceData?.loading ?? false;
      const loadError = sourceData?.error;

      // Show empty message in helper text when no options
      const helperMessage =
        loadError ||
        error ||
        (isLoading
          ? config.loadingMessage || 'Loading...'
          : options.length === 0
            ? config.emptyMessage || 'No options available'
            : getI18nString(field.helpText));

      // Multi-select mode: render card grid (Contract Package Selection)
      if (config.multiple) {
        const selectedValues = Array.isArray(value) ? (value as string[]) : [];
        const cardOptions = options.map((opt: DynamicSelectOption) => ({
          value: opt.value,
          label: opt.label,
          description:
            config.descriptionField && opt.data ? String(opt.data[config.descriptionField] ?? '') : undefined,
          icon: config.iconField && opt.data ? String(opt.data[config.iconField] ?? '') : undefined,
          data: opt.data,
        }));

        return (
          <Box>
            {getI18nString(field.label) && (
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  mb: 1.5,
                }}
              >
                {getI18nString(field.label)}
              </Typography>
            )}
            <ContractSelectionCard
              options={cardOptions}
              selectedValues={selectedValues}
              onChange={(values) => onChange(values)}
              disabled={disabled || isLoading}
              loading={isLoading}
              error={Boolean(error || loadError)}
              managementLink={config.managementLink}
              onFetchPreview={onFetchTemplatePreview}
              templatesPerOption={contractSelectionExtras?.templatesPerOption}
              selectedTemplateByOption={contractSelectionExtras?.selectedTemplateByOption}
              onTemplateSelectChange={contractSelectionExtras?.onTemplateSelectChange}
              templatesLoadingByOption={contractSelectionExtras?.templatesLoadingByOption}
              uploadCustomLink={contractSelectionExtras?.uploadCustomLink}
            />
            {helperMessage && (
              <FormHelperText error={Boolean(error || loadError)} sx={{ mt: 1 }}>
                {helperMessage}
              </FormHelperText>
            )}
          </Box>
        );
      }

      return (
        <MagicSelect
          value={stringValue}
          onChange={(e) => onChange(e.target.value)}
          label={getI18nString(field.label)}
          options={options.map((opt: DynamicSelectOption) => ({ value: opt.value, label: opt.label }))}
          helperText={helperMessage}
          error={Boolean(error || loadError)}
          disabled={disabled || isLoading || options.length === 0}
          required={field.required}
          color="primary"
        />
      );
    }

    case 'radio-cards': {
      // Radio selection displayed as clickable cards (e.g., partner type selection)
      const config = field.radioCardsConfig;
      if (!config) {
        return (
          <Typography color="error" variant="body2">
            Error: radioCardsConfig is required for radio-cards field
          </Typography>
        );
      }

      const columns = config.columns || 3;
      return (
        <FormControl error={Boolean(error)} disabled={isDisabled} required={field.required} fullWidth>
          <StyledFormLabel>{getI18nString(field.label)}</StyledFormLabel>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: columns <= 2 ? `repeat(${columns}, 1fr)` : 'repeat(2, 1fr)',
                md: `repeat(${columns}, 1fr)`,
              },
              gap: 2,
              mt: 1,
            }}
          >
            {config.options.map((option) => {
              const isSelected = value === option.value;
              // Use ButtonBase (real interactive primitive with native click
              // handling) instead of a plain `<Box onClick={...}>`. ButtonBase
              // attaches a native DOM click listener — React's synthetic-event
              // delegation has been observed to drop the first click in
              // headless Chromium with React 19, which is why the test POM
              // had to fall back to a 6-attempt React-fiber hack. Native
              // click is reliable, accessible (focus ring + Space/Enter
              // activation), and keeps `role="radio"` semantics so screen
              // readers and Playwright `getByRole('radio')` still resolve.
              return (
                // Issue #1562 / #130 adjacent-sweep: switching the radio-card from
                // `<Box onClick>` to `<ButtonBase>` so headless Chromium / React 19
                // dispatches the click cleanly without needing fiber-walk fallbacks.
                // ButtonBase handles Space + Enter natively via its intrinsic click
                // handler. Explicit onKeyDown kept as defensive fallback (HEAD —
                // covers some headless Chromium/React 19 edge cases observed in
                // wizard nav tests).
                <ButtonBase
                  key={option.value}
                  onClick={() => !disabled && onChange(option.value)}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
                      e.preventDefault();
                      onChange(option.value);
                    }
                  }}
                  disabled={disabled}
                  role="radio"
                  aria-checked={isSelected}
                  aria-label={`${option.label}${option.description ? ` - ${option.description}` : ''}`}
                  data-testid={`radio-card-${option.value}`}
                  sx={{
                    position: 'relative',
                    p: 2.5,
                    borderRadius: 2,
                    border: '1.5px solid',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    bgcolor: isSelected
                      ? (theme) =>
                          theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.08)' : 'rgba(99, 102, 241, 0.04)'
                      : 'background.paper',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    transition: 'all 0.15s ease',
                    opacity: disabled ? 0.6 : 1,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    textAlign: 'left',
                    justifyContent: 'flex-start',
                    '&:hover': {
                      borderColor: isSelected ? 'primary.main' : 'action.disabled',
                      bgcolor: isSelected
                        ? undefined
                        : (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'),
                    },
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: 2,
                    },
                    ...(option.recommended && !isSelected ? { borderColor: 'warning.main' } : {}),
                  }}
                >
                  {option.recommended && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        bgcolor: 'warning.main',
                        color: 'warning.contrastText',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Recommended
                    </Box>
                  )}
                  {option.icon && (
                    <Box
                      sx={{
                        mb: 1,
                        color: isSelected ? 'primary.main' : 'text.secondary',
                        fontSize: 0,
                        lineHeight: 0,
                        '& .MuiSvgIcon-root': { fontSize: 28 }, // Off-scale: between lg (24) and xl (32)
                      }}
                    >
                      {RADIO_CARD_ICON_MAP[option.icon] || <Typography variant="h5">{option.icon}</Typography>}
                    </Box>
                  )}
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    {option.label}
                  </Typography>
                  {option.description && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        mt: 0.5,
                      }}
                    >
                      {option.description}
                    </Typography>
                  )}
                </ButtonBase>
              );
            })}
          </Box>
          {(error || field.helpText) && (
            <FormHelperText sx={{ mt: 2 }}>{error || getI18nString(field.helpText)}</FormHelperText>
          )}
        </FormControl>
      );
    }

    case 'pricing-cards': {
      // Pricing tier card selector
      const config = field.pricingCardsConfig;
      if (!config) {
        return (
          <Typography color="error" variant="body2">
            Error: pricingCardsConfig is required for pricing-cards field
          </Typography>
        );
      }

      return (
        <FormControl error={Boolean(error)} disabled={isDisabled} required={field.required} fullWidth>
          <StyledFormLabel>{getI18nString(field.label)}</StyledFormLabel>
          <PricingCardsContainer>
            {config.tiers.map((tier) => (
              <PricingCard
                key={tier.value}
                $selected={value === tier.value}
                $recommended={tier.recommended}
                onClick={() => onChange(tier.value)}
                disabled={disabled}
                role="radio"
                aria-checked={value === tier.value}
                aria-label={`${tier.label} - ${tier.price}`}
              >
                {tier.badge && <PricingBadge>{tier.badge}</PricingBadge>}
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  {tier.label}
                </Typography>
                <PricingPrice>{tier.price}</PricingPrice>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mb: 1,
                  }}
                >
                  {tier.description}
                </Typography>
                <PricingFeatureList>
                  {tier.features.map((feature, idx) => (
                    <Box key={idx}>{feature}</Box>
                  ))}
                </PricingFeatureList>
              </PricingCard>
            ))}
          </PricingCardsContainer>
          {(error || field.helpText) && (
            <FormHelperText sx={{ mt: 2 }}>{error || getI18nString(field.helpText)}</FormHelperText>
          )}
        </FormControl>
      );
    }

    case 'summary': {
      // Read-only summary display of form data
      const config = field.summaryConfig;
      if (!config) {
        return (
          <Typography color="error" variant="body2">
            Error: summaryConfig is required for summary field
          </Typography>
        );
      }

      const formatValue = (
        fieldValue: unknown,
        format?: 'text' | 'currency' | 'date' | 'boolean' | 'list' | 'lookup' | 'percentage',
        lookupMap?: Record<string, string>,
      ): string => {
        if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
          return '—';
        }

        switch (format) {
          case 'currency':
            return `$${Number(fieldValue).toLocaleString()}`;
          case 'percentage':
            return `${Number(fieldValue)}%`;
          case 'date':
            return new Date(String(fieldValue)).toLocaleDateString();
          case 'boolean':
            return fieldValue ? 'Yes' : 'No';
          case 'list':
            return Array.isArray(fieldValue) ? fieldValue.join(', ') : String(fieldValue);
          case 'lookup':
            if (lookupMap && typeof fieldValue === 'string') {
              return lookupMap[fieldValue] || fieldValue;
            }
            // Also check lookupMaps from props
            if (lookupMaps) {
              for (const map of Object.values(lookupMaps)) {
                if (map[String(fieldValue)]) {
                  return map[String(fieldValue)];
                }
              }
            }
            return String(fieldValue);
          default:
            return String(fieldValue);
        }
      };

      return (
        <Box>
          {config.sections.map((section, sectionIdx) => (
            <SummaryContainer key={sectionIdx} sx={{ mb: sectionIdx < config.sections.length - 1 ? 2 : 0 }}>
              <SummarySectionTitle>{section.title}</SummarySectionTitle>
              {section.fields.map((summaryField) => {
                const fieldValue = allFormData?.[summaryField.name];
                return (
                  <SummaryRow key={summaryField.name}>
                    <SummaryLabel>{summaryField.label}</SummaryLabel>
                    <SummaryValue>{formatValue(fieldValue, summaryField.format, summaryField.lookupMap)}</SummaryValue>
                  </SummaryRow>
                );
              })}
            </SummaryContainer>
          ))}
        </Box>
      );
    }

    case 'password':
      return (
        <PasswordField
          value={stringValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          label={getI18nString(field.label)}
          placeholder={getI18nString(field.placeholder)}
          helperText={error || getI18nString(field.helpText)}
          error={Boolean(error)}
          disabled={isDisabled}
          required={field.required}
        />
      );

    default:
      return (
        <MagicInput
          value={stringValue}
          onChange={(e) => onChange(e.target.value)}
          label={getI18nString(field.label)}
          placeholder={getI18nString(field.placeholder)}
          helperText={error || getI18nString(field.helpText)}
          error={Boolean(error)}
          disabled={isDisabled}
          required={field.required}
          color="primary"
        />
      );
  }
};

// ============================================================================
// Component
// ============================================================================

// Helper function to evaluate a single condition
const evaluateSingleCondition = (condition: IntakeCondition, formData: Record<string, unknown>): boolean => {
  const { field: condField, operator, value: condValue } = condition;
  const fieldValue = formData[condField];

  switch (operator) {
    case 'equals':
      return fieldValue === condValue;
    case 'not_equals':
      return fieldValue !== condValue;
    case 'contains':
      return String(fieldValue || '')
        .toLowerCase()
        .includes(String(condValue).toLowerCase());
    case 'not_contains':
      return !String(fieldValue || '')
        .toLowerCase()
        .includes(String(condValue).toLowerCase());
    case 'greater_than':
      return Number(fieldValue) > Number(condValue);
    case 'less_than':
      return Number(fieldValue) < Number(condValue);
    case 'greater_than_or_equals':
      return Number(fieldValue) >= Number(condValue);
    case 'less_than_or_equals':
      return Number(fieldValue) <= Number(condValue);
    case 'is_empty':
      return fieldValue === undefined || fieldValue === null || fieldValue === '';
    case 'is_not_empty':
      return fieldValue !== undefined && fieldValue !== null && fieldValue !== '';
    case 'in':
      return (
        Array.isArray(condValue) &&
        (condValue as (string | number | boolean)[]).includes(fieldValue as string | number | boolean)
      );
    case 'not_in':
      return (
        Array.isArray(condValue) &&
        !(condValue as (string | number | boolean)[]).includes(fieldValue as string | number | boolean)
      );
    case 'starts_with':
      return String(fieldValue || '')
        .toLowerCase()
        .startsWith(String(condValue).toLowerCase());
    case 'ends_with':
      return String(fieldValue || '')
        .toLowerCase()
        .endsWith(String(condValue).toLowerCase());
    default:
      return true;
  }
};

// Helper function to evaluate condition group (AND/OR logic)
const evaluateConditionGroup = (group: IntakeConditionGroup, formData: Record<string, unknown>): boolean => {
  const results = group.conditions.map((cond) => {
    if ('logic' in cond) {
      // Nested group
      return evaluateConditionGroup(cond as IntakeConditionGroup, formData);
    }
    // Single condition
    return evaluateSingleCondition(cond as IntakeCondition, formData);
  });

  return group.logic === 'AND' ? results.every(Boolean) : results.some(Boolean);
};

export const IntakeStepRenderer: React.FC<IntakeStepRendererProps> = ({
  step,
  formData,
  onChange,
  errors = {},
  disabled = false,
  neonAccent = 'primary',
  // Analytics callbacks (P2 - Issue #471)
  onFieldFocus,
  onFieldBlur,
  // Dynamic options support (Business Creation)
  dynamicOptions,
  lookupMaps,
  onFetchTemplatePreview,
  contractSelectionExtras,
}) => {
  const { t } = useI18n();

  // Filter visible fields based on conditional display (supports both simple and AND/OR conditions)
  const visibleFields = useMemo(() => {
    return step.fields.filter((field) => {
      // Check advanced condition group first
      if (field.conditionalDisplayGroup) {
        return evaluateConditionGroup(field.conditionalDisplayGroup, formData);
      }

      // Check simple conditional display
      if (!field.conditionalDisplay) return true;

      return evaluateSingleCondition(field.conditionalDisplay, formData);
    });
  }, [step.fields, formData]);

  // Convert field width (1-12) to MUI grid size
  const getGridSize = (width?: number) => {
    // Default to full width, otherwise use the specified width (1-12)
    const w = width || 12;
    // Map to valid MUI grid sizes (1-12)
    return Math.min(12, Math.max(1, w)) as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  };

  return (
    <Grid
      container
      spacing={2.5}
      rowSpacing={3}
      sx={{
        alignItems: 'flex-start',
      }}
    >
      {visibleFields.map((field) => (
        <Grid size={{ xs: 12, sm: getGridSize(field.width) }} key={field.id}>
          <FieldContainer data-testid={field.id}>
            {/* Sensitive field indicator — absolutely positioned to avoid row misalignment */}
            {field.sensitive && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'warning.main',
                  fontSize: '0.75rem',
                  position: 'absolute',
                  top: -10,
                  right: 0,
                  zIndex: 1,
                }}
              >
                <Box component="span" sx={{ fontSize: '0.75rem' }}>
                  🔒
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'warning.main',
                  }}
                >
                  {t('intake.field.sensitiveData')}
                </Typography>
              </Box>
            )}
            {renderField({
              field,
              value: formData[field.name],
              onChange: (value) => onChange(field.name, value),
              error: errors?.[field.name],
              disabled,
              neonAccent,
              t,
              // Analytics callbacks (P2 - Issue #471)
              onFocus: onFieldFocus ? () => onFieldFocus(field.name) : undefined,
              onBlur: onFieldBlur
                ? () =>
                    onFieldBlur(
                      field.name,
                      field.type,
                      formData[field.name] !== undefined &&
                        formData[field.name] !== null &&
                        formData[field.name] !== '',
                    )
                : undefined,
              // Dynamic options support (Business Creation)
              dynamicOptions,
              lookupMaps,
              allFormData: formData,
              // Issue #1382 — inline template preview callback
              onFetchTemplatePreview,
              // Issue #1540 — two-level contract+template picker
              contractSelectionExtras,
            })}
          </FieldContainer>
        </Grid>
      ))}
    </Grid>
  );
};

IntakeStepRenderer.displayName = 'IntakeStepRenderer';

export default IntakeStepRenderer;
